import { FunctionComponent } from 'react';
import Footer from 'components/footer/Footer';
import { Link, useHistory } from 'react-router-dom';
import homeNavIcon from 'assets/images/home-nav-icon.png';
import threeStarsSvg from 'assets/svg/three-star.svg';
import bubbleSvg from 'assets/svg/bubble.svg';
import dotsSvg from 'assets/svg/dots.svg';
import groupChatGrid from 'assets/svg/group-chat-grid.svg';
import logoOrbit from 'assets/svg/logo-orbit.svg';
import hostControlCheck from 'assets/svg/host-control-check.svg';
import ArticlesAdminRow from 'views/chatScreen/articlesAdminRow/ArticlesAdminRow';
import Styles from 'views/home/Home.module.scss';

const HOST_CONTROLS_LEFT = [
  'Create A Chat Room',
  'Accept People Into Your Room',
  'Send Out Invites',
];

const HOST_CONTROLS_RIGHT = [
  'Audio And Video Control',
  'Mute Everyone',
  'Turn Off Video',
];

const Home: FunctionComponent = () => {
  const history = useHistory();

  return (
    <div className={`${Styles.page} appPageScroll`}>
      {/* Navbar */}
      <nav className={Styles.nav}>
        <div className={Styles.navInner}>
          <div className={Styles.navBrand}>
            <img src={homeNavIcon} alt="Moderate Politics" className={Styles.navIcon} />
            <span className={Styles.navName}>Moderate Politics</span>
          </div>
          <button className={Styles.navLogin} onClick={() => history.push('/login')}>
            Login
          </button>
        </div>
      </nav>

      {/* Hero — Gallup poll (HTML + Figma gradient / bubble) */}
      <section className={Styles.hero}>
        <div className={Styles.heroBg} aria-hidden="true" />
        <img src={dotsSvg} alt="" className={Styles.heroDots} aria-hidden="true" />
        <div className={Styles.heroInner}>
          <h1 className={Styles.heroTitle}>
            <span className={`${Styles.heroTitleLine} ${Styles.heroTitleLineAccent}`}>
              <span className={`${Styles.heroAccording} ${Styles.heroTitlePlain}`}>According</span>
              <span className={Styles.heroSecondLineGroup}>
                <span className={Styles.heroTitlePlain}>To A Gallop </span>
                <span className={Styles.heroTitlePlain}>Poll </span>
                <span className={Styles.heroPercent}>45%</span>
              </span>
            </span>
            <span className={`${Styles.heroTitleLine} ${Styles.heroTitleLineAccent}`}>
              <span className={Styles.heroOfUs}>Of US</span>
              <span className={Styles.heroTitlePlain}> Adults Identify As</span>
            </span>
            <span className={Styles.heroTitleLine}>Independents.</span>
          </h1>
          <div className={Styles.heroCopy}>
            <p className={Styles.heroSubtext}>
              <span className={Styles.heroSubtextLine}>
                If you combine this number with people{' '}
              </span>
              <span className={Styles.heroSubtextLine}>
                that consider themselves moderates you{' '}
              </span>
              <span className={Styles.heroSubtextLine}>
                easily have a <span className={Styles.heroGreen}>majority of voters.</span>
              </span>
            </p>
          </div>
          <div className={Styles.heroBubble}>
            <img src={bubbleSvg} alt="" className={Styles.heroBubbleShape} aria-hidden="true" />
            <div className={Styles.heroBubbleInner}>
              <p className={Styles.heroBubbleLead}>So Why Are We</p>
              <p className={Styles.heroBubbleDivided}>SO DIVIDED</p>
              <p className={Styles.heroBubbleNow}>RIGHT NOW?</p>
            </div>
          </div>
        </div>
      </section>

      {/* The Problem With Primaries */}
      <section className={Styles.primaries}>
        <h2 className={Styles.primariesTitle}>
          <span className={Styles.orange}>The Problem</span> With Primaries
        </h2>
        <div className={Styles.primariesInner}>
          <p className={Styles.primariesDesc}>
            Here lies the problem with moderates and independents, if they{' '}
            <span className={Styles.phraseRed}>
              don&apos;t
              <br />
              participate in primaries
            </span>{' '}
            then <span className={Styles.candidates}>candidates</span> with{' '}
            <span className={Styles.extremeViews}>more extreme views</span> can get on the ballot.
          </p>
          <div className={Styles.primariesCard}>
            <p className={Styles.primariesCardBody}>
              If this happens on both sides of the aisle then there is{' '}
              <span className={Styles.noSensibleCandidate}>no sensible candidate</span><br />to vote
              for. <span className={Styles.moderatesPolitics}>Moderates politics</span> was
              created to address this problem.
            </p>
            <Link to="/registration" className={Styles.primariesCta}>
              BE PART OF THE SOLUTION
              <br />
              JOIN THE CONVERSATION.
            </Link>
          </div>
        </div>
      </section>

      {/* Stars Divider */}
      <div className={Styles.divider}>
        <img src={threeStarsSvg} alt="" className={Styles.dividerImg} />
      </div>

      {/* Community — logo, copy, grid graphic, host controls */}
      <section className={Styles.community} id="community">
        <div className={Styles.communityInner}>
          <img
            src={logoOrbit}
            alt=""
            className={Styles.communityLogo}
            aria-hidden="true"
          />

          <Link to="/registration" className={Styles.communityTitle}>
            <span className={Styles.communityTitleLine}>
              Join The Community. Audio And Video
            </span>
            <span className={Styles.communityTitleLine}>
              Chat With People Nearby.
            </span>
          </Link>
          <div className={Styles.communityMediaBlock}>
            <p className={Styles.communitySub}>View the conversation topics and join in.</p>

            <div className={Styles.communityBottomBlock}>
              <img
                src={groupChatGrid}
                alt="Video chat with host and nearby participants"
                className={Styles.communityGridImg}
              />

              <div className={Styles.hostControls}>
                <h3 className={Styles.hostControlsTitle}>Host Controls:</h3>
                <ul className={Styles.hostControlsCol}>
                  {HOST_CONTROLS_LEFT.map((item) => (
                    <li key={item} className={Styles.hostControlsItem}>
                      <img
                        src={hostControlCheck}
                        alt=""
                        className={Styles.hostControlsCheck}
                        aria-hidden="true"
                      />
                      {item}
                    </li>
                  ))}
                </ul>
                <ul className={Styles.hostControlsCol}>
                  {HOST_CONTROLS_RIGHT.map((item) => (
                    <li key={item} className={Styles.hostControlsItem}>
                      <img
                        src={hostControlCheck}
                        alt=""
                        className={Styles.hostControlsCheck}
                        aria-hidden="true"
                      />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className={Styles.infoRowSection} aria-label="Articles and admin messages">
        <div className={Styles.infoRowInner}>
          <ArticlesAdminRow publicMode />
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
